<template>
  <div>
    <BulkUploadConfirmationDialog
      :opened.sync="bulkUploadConfirmationDialogOpened"
      :documents.sync="documentsToImport"
      @proceed="uploadBulkDocumentForAutoSplitting"
    />

    <v-dialog
      max-width="1000px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="previewDialogOpened"
      @keydown.esc="
        previewDialogOpened = false;
        documentPreviewData = '';
        previewRotation = 0;
      "
    >
      <v-card v-if="selectedDocumentLink && previewType">
        <v-card-title class="title">
          <span class="text-truncate" style="max-width: 600px">{{
            getDocumentName(selectedDocumentLink)
          }}</span>

          <v-spacer />
          <v-tooltip bottom class="mr-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="previewRotation += 90"
              slot="activator"
            >
              <v-icon size="25"> rotate_right </v-icon>
            </v-btn>
            <span>Rotate</span>
          </v-tooltip>
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0 ml-1"
            @click="
              previewDialogOpened = false;
              documentPreviewData = '';
              previewRotation = 0;
            "
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="documentPreviewData && previewLoaded">
          <preview-document
            :document-preview-data="documentPreviewData"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
          />
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <NewDocumentSplitterDialog
      :opened.sync="documentSplitterDialogOpened"
      :currentCompany="currentCompany"
      :parentDocument="parentDocumentForSplitting"
      :childDocuments="splittedChildDocuments"
      :allUploadQuestions="allUploadQuestions"
      @refresh-questionnaire="
        $emit('refresh-questionnaire-until-documents-are-processed')
      "
    />
    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <div v-if="bulkUploadProcessor">
      <h2 class="section-title text-xs-left mt-1 d-flex align-center">
        Bulk Upload Processor
        <v-chip small color="warning" class="ml-2" style="flex: 0 !important"
          >Beta</v-chip
        >
      </h2>
      <v-alert
        :value="true"
        outline
        block
        :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
        color="info"
        class="text-xs-left"
      >
        <div class="text-xs-left">
          The <b>Bulk Upload Processor</b> tool splits multiple documents into
          individual ones and matches them with checklist items. Soraban AI may
          make errors, so review processed pages. This flow might change based
          on feedback.
        </div>
      </v-alert>
    </div>

    <v-layout class="mb-2" wrap v-if="bulkUploadProcessor">
      <v-btn
        block
        @click="pickDocuments"
        class="primary hidden-sm-and-up text-none mb-3"
      >
        Choose Files or Take Picture
      </v-btn>

      <v-flex @drop.prevent="addFile" @dragover.prevent class="hidden-xs-only">
        <v-layout class="drag-and-drop" style="min-height: 200px">
          <v-layout column align-center justify-center v-if="uploading">
            <v-progress-circular
              indeterminate
              size="40"
              class="primary--text ma-5"
            />
          </v-layout>
          <v-layout column align-center justify-center v-else>
            <div>
              <v-icon>upload_file</v-icon>
            </div>
            <div>
              <span @click="pickDocuments" class="drag-and-drop-text"
                >Drag and drop files here or
                <span class="clickable primary--text">browse</span></span
              >
            </div>
          </v-layout>
        </v-layout>
      </v-flex>
    </v-layout>

    <v-layout class="text-xs-left" column>
      <v-layout
        column
        class="container"
        v-if="bulkDocumentsThatNeedSplitting.length > 0"
      >
        <h5 class="title">
          <v-icon class="mr-2 material-icons-outlined" color="primary">
            auto_awesome
          </v-icon>
          <div>
            <span>Suggestions: </span>
            <span v-if="bulkDocumentsThatNeedSplitting.length === 1">
              There is 1 document need splitting.
            </span>
            <span v-else-if="bulkDocumentsThatNeedSplitting.length > 1">
              There are {{ bulkDocumentsThatNeedSplitting.length }} documents
              need splitting.
            </span>
          </div>
        </h5>

        <v-layout
          v-for="doc in bulkDocumentsThatNeedSplitting"
          :key="doc.id"
          :class="doc.selected_options ? 'suggestion-item' : 'bulk-item'"
        >
          <span class="mr-1 grey--text">•</span>
          <v-icon
            class="mr-1 material-icons-outlined grey--text text--darken-2"
          >
            insert_drive_file
          </v-icon>

          <v-layout column class="pl-1">
            <div class="file-name">
              {{ getDocumentName(doc.source) }}
              <v-chip
                small
                v-if="doc.status === 'processing'"
                class="ml-2 white--text primary"
              >
                <v-progress-circular
                  indeterminate
                  size="18"
                  class="mr-2"
                  color="white"
                ></v-progress-circular>
                Processing...
              </v-chip>
              <v-icon
                v-else-if="doc.status === 'review_required'"
                color="success darken-1"
                class="ml-1"
                size="20"
              >
                check
              </v-icon>
            </div>
            <div class="detail" v-if="doc.selected_options">
              This file might contain {{ formatOptions(doc.selected_options) }}
            </div>
            <div class="detail" v-else-if="doc.status === 'review_required'">
              <span v-if="childDocuments(doc).length > 0">
                Documents recognized:
                {{
                  childDocuments(doc)
                    .map(childDoc =>
                      childDoc.detailed_name
                        ? childDoc.detailed_name
                        : childDoc.name,
                    )
                    .join(', ')
                }}
              </span>
              <span v-else-if="doc.detailed_name">
                Document recognized: {{ doc.detailed_name }}
              </span>
              <span v-else> No documents recognized. </span>
            </div>
          </v-layout>

          <v-btn
            v-if="doc.selected_options"
            class="ml-3 px-3 text-none"
            color="primary"
            small
            contained
            @click="openSplitterDialog(doc)"
          >
            Split PDF
          </v-btn>
          <v-icon
            @click="deleteExtractedDocument(doc.id)"
            style="cursor: pointer"
            color="error darken-1"
            class="ml-1"
            size="20"
            >delete</v-icon
          >
        </v-layout>
      </v-layout>
      <v-expansion-panel
        class="mt-2 expansion-panel"
        expand
        :value="[true]"
        v-if="!isEmpty(currentQuestionnaireBulkDocuments)"
      >
        <v-expansion-panel-content :value="uploadedBulkDocuments">
          <template v-slot:header>
            <v-layout row align-center>
              <div class="pr-2">
                <v-icon
                  large
                  class="darkgrey--text text--lighten-1 material-icons-outlined"
                  >file_copy</v-icon
                >
              </div>
              <span class="document-item-text">Bulk Upload</span>
            </v-layout>
          </template>

          <v-card>
            <v-card-text class="pt-0">
              <v-layout
                align-center
                class="file-item mx-2 mb-2 px-3 py-2 text-xs-left body-1"
                v-for="(document, i) in currentQuestionnaireBulkDocuments"
                :key="document.id"
                @click="previewDocument(formatPreviewLink(document))"
              >
                <v-icon
                  class="mr-1 material-icons-outlined grey--text text--darken-2"
                >
                  insert_drive_file
                </v-icon>

                <v-layout column class="pl-1">
                  <div class="file-name">
                    {{ getDocumentName(document.source) }}
                    <v-chip
                      small
                      v-if="document.status === 'processing'"
                      class="ml-2 white--text primary"
                    >
                      <v-progress-circular
                        indeterminate
                        size="18"
                        class="mr-2"
                        color="white"
                      ></v-progress-circular>
                      Processing...
                    </v-chip>
                    <v-chip
                      small
                      v-else-if="document.status === 'not_exportable'"
                      class="ml-2 warning"
                    >
                      Not Processable
                    </v-chip>
                  </div>

                  <div
                    class="detail"
                    v-if="document.status === 'identify_failed'"
                  >
                    <div class="body-1">
                      <v-icon color="error darken-1" size="20">
                        error
                      </v-icon>
                      <span class="grey--text text--darken-4">
                        We aren't able to identify this document at this moment.
                        Please try again.
                      </span>
                    </div>
                    <v-layout align-center class="mt-1">
                      <v-btn
                        small
                        color="primary"
                        outlined
                        @click.stop="identifyAndMatchDocument(document.id)"
                        class="px-3 mx-0 text-none"
                        :disabled="processing.includes(document.id)"
                      >
                        Retry
                      </v-btn>
                    </v-layout>
                  </div>

                  <div class="detail" v-if="document.status === 'split_failed'">
                    <div class="body-1">
                      <v-icon color="error darken-1" size="20">
                        error
                      </v-icon>
                      <span class="grey--text text--darken-4">
                        We aren't able to process this document. Please manually
                        review this one.
                      </span>
                    </div>
                    <v-layout align-center class="mt-1">
                      <v-btn
                        small
                        color="primary"
                        outlined
                        @click.stop="autoSplitDocument(document.id)"
                        class="px-3 mx-0 text-none"
                        :disabled="processing.includes(document.id)"
                      >
                        Retry
                      </v-btn>
                      <div class="mx-2">
                        or
                      </div>
                      <v-btn
                        small
                        color="primary"
                        @click.stop="openSplitterDialog(document)"
                        class="px-3 mx-0 text-none"
                        :disabled="processing.includes(document.id)"
                      >
                        Split Manually
                      </v-btn>
                    </v-layout>
                  </div>

                  <div
                    class="detail"
                    v-else-if="
                      document.status === 'review_required' ||
                        document.status === 'split_succeeded' ||
                        document.status === 'identify_succeeded'
                    "
                  >
                    <div class="body-1">
                      <template v-if="document.status === 'review_required'">
                        <v-icon color="info darken-1" size="20">
                          info
                        </v-icon>
                        <span class="grey--text text--darken-4">
                          We have successfully processed this document. Please
                          click the button below to review.
                        </span>
                      </template>

                      <template
                        v-else-if="
                          document.status === 'split_succeeded' &&
                            document.source.endsWith('.pdf')
                        "
                      >
                        <v-icon color="success darken-1" size="20">
                          check_circle
                        </v-icon>
                        <span class="grey--text text--darken-4">
                          This document has been processed but you can still
                          correct it
                        </span>
                      </template>

                      <template v-else>
                        <v-icon color="success darken-1" size="20">
                          check_circle
                        </v-icon>
                        <span class="grey--text text--darken-4">
                          This document has been identified successfully and is
                          matched with the checklist.
                        </span>
                      </template>
                    </div>

                    <div class="pl-4">
                      <span v-if="childDocuments(document).length > 0">
                        Documents recognized:
                        {{
                          childDocuments(document)
                            .map(childDoc =>
                              childDoc.detailed_name
                                ? childDoc.detailed_name
                                : childDoc.name,
                            )
                            .join(', ')
                        }}
                      </span>
                      <span v-else-if="document.detailed_name">
                        Document recognized: {{ document.detailed_name }}
                      </span>
                      <span v-else> No documents recognized. </span>
                    </div>

                    <v-layout
                      align-center
                      class="mt-1"
                      v-if="document.source.endsWith('.pdf')"
                    >
                      <v-btn
                        small
                        color="primary"
                        :outline="document.status === 'split_succeeded'"
                        @click.stop="openSplitterDialog(document)"
                        class="px-3 mx-0 text-none"
                      >
                        {{
                          document.status === 'review_required'
                            ? 'Review'
                            : 'Edit'
                        }}
                      </v-btn>

                      <v-layout v-if="getUnrecognizedPageCount(document) > 0">
                        <v-icon class="pl-3 pr-1" small color="warning"
                          >warning</v-icon
                        >
                        <span class="grey--text">
                          There
                          {{
                            getUnrecognizedPageCount(document) === 1
                              ? 'is 1 page'
                              : `are ${getUnrecognizedPageCount(
                                  document,
                                )} pages`
                          }}
                          that are not yet classified.
                        </span>
                      </v-layout>
                    </v-layout>
                  </div>
                </v-layout>

                <v-btn
                  flat
                  icon
                  @click="deleteExtractedDocument(document.id)"
                  @click.native.stop
                  :loading="deletingDocIds.includes(document.id)"
                  color="error"
                >
                  <v-icon size="20">delete</v-icon>
                </v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-layout>
  </div>
</template>

<script>
import NewDocumentSplitterDialog from '@/components/dialogs/NewDocumentSplitterDialog';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import API from '@/api';
import has from 'lodash/has';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import NewDocumentChecklistMixin from '@/mixins/NewDocumentChecklistMixin';
import PreviewDocument from '@/components/PreviewDocument';
import DocumentMixin from '@/mixins/DocumentMixin';
import QuestionnaireMixin from '@/mixins/QuestionnaireMixin';
import UtilsMixins from '@/mixins/UtilsMixins';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';
import BulkUploadConfirmationDialog from '@/components/dialogs/BulkUploadConfirmationDialog';
// import posthog from 'posthog-js';

export default {
  components: {
    NewDocumentSplitterDialog,
    PreviewDocument,
    BulkUploadConfirmationDialog,
  },

  mixins: [
    NewDocumentChecklistMixin,
    QuestionnaireMixin,
    DocumentMixin,
    UtilsMixins,
    FirmPreferenceMixins,
  ],

  props: {
    currentCompany: {
      type: Object,
    },

    currentQuestionnaire: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      uploading: false,
      documentsToImport: [],
      uploadedBulkDocuments: true,
      unrecognizedPages: null,
      deletingDocIds: [],
      bulkUploadConfirmationDialogOpened: false,

      // For document splitter;
      documentSplitterDialogOpened: false,
      parentDocumentForSplitting: null,
      splittedChildDocuments: [],
      processing: [],
    };
  },

  computed: {
    currentQuestionnaireBulkDocuments() {
      return this.currentQuestionnaire.extracted_documents.filter(doc => {
        return (
          doc.document_type === 'bulk_document' &&
          doc.source.toLowerCase().endsWith('.pdf')
        );
      });
    },

    bulkDocumentsThatNeedSplitting() {
      return this.currentQuestionnaire.extracted_documents.filter(doc => {
        return (
          doc.selected_options &&
          doc.selected_options.length > 1 &&
          doc.source.toLowerCase().endsWith('.pdf')
        );
      });
    },

    allUploadQuestions() {
      return this.currentQuestionnaire.questionnaire_questions.filter(
        q => q.question_type === 'upload',
      );
    },

    optionSuggestions() {
      return uniq(
        this.allUploadQuestions.flatMap(question => {
          return question.options || [];
        }),
      );
    },
  },

  methods: {
    isEmpty,

    getUnrecognizedPages(doc) {
      // If the document has a detailed_name, it is already recognized and has no child documents.
      if (doc.detailed_name) {
        return [];
      }

      if (doc.page_count) {
        return Array(doc.page_count)
          .fill(null)
          .map((_, index) => {
            const page = index + 1;
            return page;
          })
          .filter(page => {
            return !this.childDocuments(doc).some(childDoc => {
              return childDoc.parent_pages.includes(page);
            });
          });
      } else {
        return [];
      }
    },

    getUnrecognizedPageCount(doc) {
      return this.getUnrecognizedPages(doc).length;
    },

    async deleteExtractedDocument(id) {
      try {
        this.deletingDocIds.push(id);
        await API.extractedDocument.deleteExtractedDocument(id);

        this.$emit('refresh-questionnaire');
      } catch (e) {
        console.log(e);
        this.deletingDocIds = this.deletingDocIds.filter(d => d !== id);
      }
    },

    formatPreviewLink(document) {
      return document.source
        .split('/')
        .slice(3)
        .join('/');
    },

    async openSplitterDialog(doc) {
      this.parentDocumentForSplitting = doc;
      this.splittedChildDocuments = this.childDocuments(doc);
      this.unrecognizedPages = this.getUnrecognizedPages(doc);
      this.documentSplitterDialogOpened = true;
    },

    formatOptions(options) {
      const formatter = new Intl.ListFormat('en', {
        style: 'long',
        type: 'conjunction',
      });
      return formatter.format(options);
    },

    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    async uploadBulkDocumentForAutoSplitting() {
      this.bulkUploadConfirmationDialogOpened = false;
      try {
        this.uploading = true;
        let path;
        if (isEmpty(this.currentQuestionnaire.document_path)) {
          path = '';
        } else {
          path = this.currentQuestionnaire.document_path
            .replace('{year}', this.currentQuestionnaire.tax_year)
            .replace('{section}', 'Bulk Uploads')
            .replace('{questionnaire_name}', this.currentQuestionnaire.name);
        }
        this.importDocumentsDialogOpened = false;
        const companyId = this.currentCompany.id;
        let success = 0;

        for (const documentToImport of this.documentsToImport) {
          const trueExtension = await this.getTrueExtension(documentToImport);
          const currentExtension = this.getExtensionOfFilename(
            documentToImport.name,
          );
          const extensionsToCheck = ['pdf', 'png', 'gif', 'jpeg'];

          if (documentToImport.size === 0) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be EMPTY. Kindly check those files and try uploading them again.`,
            );
          }

          if (
            extensionsToCheck.includes(currentExtension) &&
            trueExtension !== currentExtension
          ) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.`,
            );
          }

          if (currentExtension === 'pdf') {
            if (await this.pdfHasPassword(documentToImport)) {
              throw new Error(
                `The PDFs you uploaded are password-protected. Please either remove the password or upload alternative files.`,
              );
            }
          }

          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId,
          });

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.integration.processDocument({
            id,
            documentName: documentToImport.name,
            documentType: 'bulk_document',
            questionnaireId: this.currentQuestionnaire.id,
            path: '/'.concat(path),
            companyId,
            requiresSplitting: documentToImport.requiresSplitting,
          });

          // posthog.capture('uploaded document', {
          //   source: 'questionnaire bulk upload',
          // });

          if (response.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: `There was a problem uploading ${documentToImport.name}.`,
            });
          } else {
            success += 1;
          }
        }

        if (this.documentsToImport.length === success) {
          this.$store.dispatch('application/setSuccessNotification', {
            message: `You have successfully uploaded ${
              this.documentsToImport.length
            } document${this.documentsToImport.length > 1 ? 's' : ''}!`,
          });
        }
        this.$emit('refresh-questionnaire-until-documents-are-processed');
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: has(e, 'message')
            ? get(e, 'message')
            : `There was a problem uploading the document${
                this.documentsToImport.length > 1 ? 's' : ''
              }.`,
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.documentsToImport = [];
        this.uploading = false;
      }
    },

    async addFile(e) {
      const documents = e.dataTransfer.files;

      if (documents) {
        this.documentsToImport.push(...documents);
        this.bulkUploadConfirmationDialogOpened = true;
      }
    },

    async onDocumentsPicked(e) {
      const documents = e.target.files;
      if (documents) {
        this.documentsToImport.push(...documents);
        this.bulkUploadConfirmationDialogOpened = true;
      }
    },

    async identifyAndMatchDocument(extractedDocumentId) {
      if (this.processing.includes(extractedDocumentId)) {
        return;
      }

      try {
        this.processing.push(extractedDocumentId);

        const response = API.integration.identifyAndMatchDocument({
          extractedDocumentId,
          companyId: this.currentCompany.id,
        });

        this.$emit('refresh-questionnaire-until-documents-are-processed');
      } finally {
        // Allow retrying again after 5 seconds
        setTimeout(() => {
          this.processing = this.processing.filter(
            id => id !== extractedDocumentId,
          );
        }, 5000);
      }
    },

    async autoSplitDocument(extractedDocumentId) {
      if (this.processing.includes(extractedDocumentId)) {
        return;
      }

      try {
        this.processing.push(extractedDocumentId);

        const response = API.integration.autoSplitDocuments({
          extractedDocumentId,
          companyId: this.currentCompany.id,
        });

        this.$emit('refresh-questionnaire-until-documents-are-processed');
      } finally {
        // Allow retrying again after 5 seconds
        setTimeout(() => {
          this.processing = this.processing.filter(
            id => id !== extractedDocumentId,
          );
        }, 5000);
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.container {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0;
  margin-bottom: 16px;
}

.title {
  font-weight: bold;
  font-size: 17px !important;
  border-bottom: 1px solid #ccc;
  padding: 16px;
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
}

.suggestion-item {
  padding: 8px 20px;
  align-items: center;
  border-top: 1px solid #ccc;
}
.bulk-item {
  padding: 8px 20px;
  align-items: center;
}

.file-name {
  font-size: 16px;
  font-weight: bold;
}

.detail {
  font-size: 12px;
  color: #666;
}

.drag-and-drop {
  height: 140px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);

  & >>> i {
    color: rgba(0, 0, 0, 0.3);
  }

  & >>> span {
    color: rgba(0, 0, 0, 0.6);
  }

  & >>> span.clickable {
    cursor: pointer;
  }
}
.drag-and-drop-text {
  font-size: 14px;
}
.file-item {
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background: #fff;
  cursor: pointer;

  &:hover {
    background: #f1f1f1;
  }
}
.document-item-text {
  margin-top: 6px;
  font-size: 16px;

  & >>> p {
    margin-bottom: 8px;
  }
}

.expansion-panel {
  border: none;
  box-shadow: none;

  & >>> .v-expansion-panel__container {
    border: 1px solid rgba(0, 0, 0, 0.2) !important;
    border-radius: 4px;
    margin-bottom: 12px;
    overflow: hidden;
  }

  & >>> .v-expansion-panel__container.outlined-red {
    border: 1px solid red !important;
  }
}
</style>
